/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  FormControl,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Delete } from "@mui/icons-material";
import Drawer from "@mui/material/Drawer";
import style from "../../../styles/common/formModal.module.css";
import EditDialog from "../../../Components/Common/EditDialog";
import FileField from "../../../Components/Masters/FileField";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";
import { useUser } from "../../../context/AuthContext";
import Multiline from "../../../Components/Masters/Multiline";
import { get } from "../../../utils/api";
import { FUEL_TYPE_OPTIONS, FUEL_UNIT_BY_TYPE } from "../../../utils/fuelConstants";
import { wasteTypesArray } from "../../../utils/constant";

const QUANTITY_SOLD_UNITS = ["Pieces", "MT", "KL"];

const emptyProcessingMethodRow = () => ({ methodName: "", quantity: "" });

/** Customer master `processingMethodsUsed` → string[] for form + API */
const normalizeProcessingMethodsUsed = (v) => {
  if (Array.isArray(v)) {
    return v.map((x) => String(x ?? "").trim()).filter(Boolean);
  }
  if (typeof v === "string" && v.trim()) {
    return [v.trim()];
  }
  return [];
};

/** Up to two processing methods — same row pattern as fuel entries. */
const normalizeProcessingMethodEntries = (initialData) => {
  const raw = Array.isArray(initialData?.processingMethodEntries)
    ? initialData.processingMethodEntries
    : [];

  if (raw.length > 0) {
    const mapped = raw.slice(0, 2).map((entry) => ({
      methodName: (entry?.methodName || entry?.processingMethod || "").trim(),
      quantity:
        entry?.quantity !== undefined && entry?.quantity !== null
          ? String(entry.quantity)
          : "",
    }));
    if (mapped.length === 2) {
      const secondEmpty =
        !mapped[1].methodName && !String(mapped[1].quantity ?? "").trim();
      return secondEmpty ? [mapped[0]] : mapped;
    }
    return mapped.length ? mapped : [emptyProcessingMethodRow()];
  }

  const methods = normalizeProcessingMethodsUsed(initialData?.processingMethodUsed);
  if (methods.length > 0) {
    const fromMethods = methods
      .slice(0, 2)
      .map((methodName) => ({ methodName, quantity: "" }))
      .filter((entry) => wasteTypesArray.includes(entry.methodName));
    if (fromMethods.length > 0) {
      return fromMethods;
    }
  }

  const legacyMaterials = parseLegacyMaterialsEntries(
    initialData?.materialsAddedDuringProcessing
  );
  if (legacyMaterials.length > 0) {
    return legacyMaterials;
  }

  return [emptyProcessingMethodRow()];
};

const parseLegacyMaterialsEntries = (value) => {
  const allowed = new Set(wasteTypesArray);
  const toEntry = (token) => {
    const trimmed = String(token ?? "").trim();
    if (!trimmed) return null;

    const qtyMatch = trimmed.match(/^(.+?)\s+(\d+(?:\.\d+)?)\s*MT$/i);
    if (qtyMatch) {
      const methodName = qtyMatch[1].trim();
      if (!allowed.has(methodName)) return null;
      return { methodName, quantity: qtyMatch[2] };
    }

    if (allowed.has(trimmed)) {
      return { methodName: trimmed, quantity: "" };
    }
    return null;
  };

  if (Array.isArray(value)) {
    return value
      .flatMap((item) =>
        String(item).includes(",")
          ? String(item).split(/,\s*/)
          : [String(item)]
      )
      .map(toEntry)
      .filter(Boolean)
      .slice(0, 2);
  }

  if (typeof value === "string" && value.trim()) {
    return value
      .split(/,\s*/)
      .map(toEntry)
      .filter(Boolean)
      .slice(0, 2);
  }

  return [];
};

const unitMenuProps = {
  PaperProps: {
    style: { maxHeight: "55%", overflowY: "auto" },
  },
};

const formatProcessingMethodsDisplay = (arr) =>
  Array.isArray(arr) && arr.length > 0 ? arr.join(", ") : "";

const parseQuantitySold = (initialData) => {
  const raw = initialData?.quantitySold;
  if (typeof raw === "object" && raw !== null) {
    const unit = raw.unit != null ? String(raw.unit).trim() : "";
    return { value: raw.value ?? "", unit };
  }
  if (typeof raw === "string" && raw.trim()) {
    const parts = raw.trim().split(" ");
    if (parts.length >= 2) {
      return { value: parts[0], unit: parts.slice(1).join(" ") };
    }
    return { value: parts[0], unit: "" };
  }
  if (raw !== undefined && raw !== null && raw !== "") {
    return { value: String(raw).trim(), unit: "" };
  }
  return { value: "", unit: "" };
};

const ENERGY_SOURCE_ELECTRICITY = "electricity";
const ENERGY_SOURCE_FUEL = "fuel";

const deriveEnergySourcesFromInitial = (initialData) => {
  const saved = initialData?.energySources;
  if (Array.isArray(saved) && saved.length > 0) {
    const allowed = new Set([ENERGY_SOURCE_ELECTRICITY, ENERGY_SOURCE_FUEL]);
    const picked = saved.filter((s) => allowed.has(s));
    if (picked.length > 0) return [...new Set(picked)];
  }

  const elecStr = String(
    initialData?.electricityConsumption ??
    initialData?.energyConsumptionForProcessing ??
    ""
  ).trim();
  const hasElec = elecStr !== "";

  const raw = Array.isArray(initialData?.fuelEntries) ? initialData.fuelEntries : [];
  const hasFuelInEntries = raw.some((f) =>
    String(f?.fuelType || f?.fuelName || "").trim()
  );
  const hasLegacyFuel = Boolean(
    String(initialData?.fuelType || "").trim() ||
    (initialData?.fuelConsumption !== undefined &&
      initialData?.fuelConsumption !== null &&
      String(initialData.fuelConsumption).trim() !== "")
  );
  const hasFuel = hasFuelInEntries || hasLegacyFuel;

  if (hasElec && hasFuel) return [ENERGY_SOURCE_ELECTRICITY, ENERGY_SOURCE_FUEL];
  if (hasElec) return [ENERGY_SOURCE_ELECTRICITY];
  if (hasFuel) return [ENERGY_SOURCE_FUEL];
  return [];
};

const emptyFuelRow = () => ({ fuelName: "", quantity: "", unit: "" });

/** Up to two fuels — same shape as Product Master (`fuelName`, `quantity`, `unit`). */
const normalizeFuelEntries = (initialData) => {
  const raw = Array.isArray(initialData?.fuelEntries)
    ? initialData.fuelEntries
    : [];
  if (raw.length > 0) {
    const mapped = raw.slice(0, 2).map((f) => ({
      fuelName: (f?.fuelType || f?.fuelName || "").trim(),
      quantity:
        f?.fuelConsumption !== undefined && f?.fuelConsumption !== null
          ? String(f.fuelConsumption)
          : f?.quantity !== undefined && f?.quantity !== null
            ? String(f.quantity)
            : "",
      unit: (f?.fuelConsumptionUnit || f?.unit || "").trim(),
    }));
    if (mapped.length === 2) {
      const secondEmpty =
        !mapped[1].fuelName &&
        !String(mapped[1].quantity ?? "").trim() &&
        !mapped[1].unit;
      return secondEmpty ? [mapped[0]] : mapped;
    }
    return mapped.length ? mapped : [emptyFuelRow()];
  }
  if (
    initialData?.fuelType ||
    initialData?.fuelConsumption !== undefined ||
    initialData?.fuelConsumptionUnit
  ) {
    return [
      {
        fuelName: (initialData?.fuelType || "").trim(),
        quantity:
          initialData?.fuelConsumption !== undefined &&
            initialData?.fuelConsumption !== null
            ? String(initialData.fuelConsumption)
            : "",
        unit: (initialData?.fuelConsumptionUnit || "").trim(),
      },
    ];
  }
  return [emptyFuelRow()];
};

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const Form = ({
  isOpen,
  onClose,
  onSubmit,
  header,
  initialData,
  isEditing,
  id,
}) => {
  const { user } = useUser();
  const qty = parseQuantitySold(initialData);

  const buildInitialFormData = () => ({
    soldProductDate: toDateInputValue(
      initialData?.soldProductDate || initialData?.createdAtOriginal || initialData?.createdAt
    ),
    productMasterId: initialData?.productMasterId || initialData?.productId?._id || "",
    productId: initialData?.productIdSku || initialData?.productId || "",
    productName: initialData?.productName || "",
    productCategory: initialData?.productCategory || "",
    customerMasterId: initialData?.customerMasterId || "",
    vendorName: initialData?.vendorName || "",
    quantitySold: qty.value,
    quantitySoldUnit: qty.unit,
    locationOfProcessing: initialData?.locationOfProcessing || "",
    // processingMethodUsed: normalizeProcessingMethodsUsed(initialData?.processingMethodUsed),
    electricityConsumption:
      initialData?.electricityConsumption ??
      initialData?.energyConsumptionForProcessing ??
      "",
    fuelEntries: normalizeFuelEntries(initialData),
    processingMethodEntries: normalizeProcessingMethodEntries(initialData),
    remark: initialData?.remark || "",
    documents: initialData?.documents || [],
    editRemarkReason: initialData?.editRemarkReason || "",
    energySources: deriveEnergySourcesFromInitial(initialData),
  });

  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState(buildInitialFormData);
  const [formData2, setFormData2] = useState(buildInitialFormData);
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [productsWithCustomers, setProductsWithCustomers] = useState([]);
  const inlineErrorTextProps = {
    sx: { ml: 0, mr: 0, mt: "4px", fontSize: "0.95rem", lineHeight: 1.2 },
  };

  const disabledFieldSx = {
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "#6b7280",
      cursor: "not-allowed",
    },
    "& .MuiInputBase-root.Mui-disabled": {
      backgroundColor: "#f3f4f6",
    },
  };

  useEffect(() => {
    const next = buildInitialFormData();
    setFormData(next);
    setFormData2(next);
    setUploadedFiles(initialData?.documents || []);
    setErrors({});
  }, [id, initialData, isOpen]);

  useEffect(() => {
    if (!productsWithCustomers.length || formData.productMasterId) return;

    const matchProduct = productsWithCustomers.find(
      (p) =>
        p._id === formData.productMasterId ||
        (formData.productId && p.productId === formData.productId) ||
        (formData.productName && p.productName === formData.productName)
    );
    if (!matchProduct) return;

    const matchCustomer = (matchProduct.customers || []).find(
      (c) =>
        c._id === formData.customerMasterId ||
        (formData.vendorName && c.customerVendorName === formData.vendorName)
    );

    setFormData((prev) => ({
      ...prev,
      productMasterId: matchProduct._id,
      productId: matchProduct.productId || prev.productId,
      productName: matchProduct.productName || prev.productName,
      productCategory: matchProduct.category || prev.productCategory,
      customerMasterId: matchCustomer?._id || prev.customerMasterId,
      vendorName: matchCustomer?.customerVendorName || prev.vendorName,
      locationOfProcessing: matchCustomer?.country || prev.locationOfProcessing,
      // processingMethodUsed: matchCustomer
      //   ? normalizeProcessingMethodsUsed(matchCustomer.processingMethodsUsed)
      //   : prev.processingMethodUsed,
    }));
  }, [productsWithCustomers]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchProducts = async () => {
      try {
        const res = await get("/master/customerMasters/products-with-customers");
        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];
        setProductsWithCustomers(list);
      } catch (error) {
        console.error("Error fetching products with customers:", error);
        setProductsWithCustomers([]);
      }
    };
    fetchProducts();
  }, [isOpen]);

  const selectedProduct = useMemo(
    () =>
      productsWithCustomers.find((p) => p._id === formData.productMasterId) || null,
    [productsWithCustomers, formData.productMasterId]
  );

  const customerOptions = useMemo(
    () => selectedProduct?.customers || [],
    [selectedProduct]
  );

  const materialOptions = useMemo(() => {
    const savedNames = (formData.processingMethodEntries || [])
      .map((entry) => String(entry?.methodName ?? "").trim())
      .filter(Boolean);
    const extras = savedNames.filter((name) => !wasteTypesArray.includes(name));
    return [...extras, ...wasteTypesArray];
  }, [formData.processingMethodEntries]);

  const applySelectedProduct = (product) => {
    if (!product) return;
    setFormData((prev) => ({
      ...prev,
      productMasterId: product._id || "",
      productId: product.productId || "",
      productName: product.productName || "",
      productCategory: product.category || "",
      customerMasterId: "",
      vendorName: "",
      locationOfProcessing: "",
      processingMethodEntries: [emptyProcessingMethodRow()],
    }));
    setErrors((prev) => ({
      ...prev,
      productMasterId: "",
      productId: "",
      productName: "",
      customerMasterId: "",
      vendorName: "",
      locationOfProcessing: "",
      processingMethodEntries: "",
    }));
  };

  const applySelectedCustomer = (customer) => {
    if (!customer) return;
    setFormData((prev) => ({
      ...prev,
      customerMasterId: customer._id || "",
      vendorName: customer.customerVendorName || "",
      locationOfProcessing: customer.country || "",
      processingMethodEntries: [emptyProcessingMethodRow()],
    }));
    setErrors((prev) => ({
      ...prev,
      customerMasterId: "",
      vendorName: "",
      locationOfProcessing: "",
      processingMethodEntries: "",
    }));
  };

  const handleExitAgree = () => {
    setEditDialog(false);
    setErrors({});
    const cleared = buildInitialFormData();
    setFormData(cleared);
    setFormData2(cleared);
    onClose();
  };

  const handleClose = () => {
    const isFormDataUnchanged = Object.keys(formData).every((key) => {
      const value1 = formData[key];
      const value2 = formData2[key];
      if (Array.isArray(value1) && Array.isArray(value2)) {
        return JSON.stringify(value1) === JSON.stringify(value2);
      }
      if (
        typeof value1 === "object" &&
        value1 !== null &&
        typeof value2 === "object" &&
        value2 !== null
      ) {
        return JSON.stringify(value1) === JSON.stringify(value2);
      }
      return value1 === value2;
    });

    if (isFormDataUnchanged) handleExitAgree();
    else setEditDialog(true);
  };

  const validateFormData = () => {
    const newErrors = {};

    if (!formData.soldProductDate) {
      newErrors.soldProductDate = "Sold product date is required.";
    }
    if (!formData.productMasterId) {
      newErrors.productMasterId = "Product is required.";
    }
    if (!formData.customerMasterId) {
      newErrors.customerMasterId = "Customer/Vendor Name is required.";
    }
    if (!String(formData.quantitySold ?? "").trim()) {
      newErrors.quantitySold = "Quantity Sold is required.";
    } else if (isNaN(Number(formData.quantitySold)) || Number(formData.quantitySold) < 0) {
      newErrors.quantitySold = "Quantity Sold must be a valid number.";
    }
    if (!formData.quantitySoldUnit) {
      newErrors.quantitySoldUnit = "Unit is required.";
    }
    // const methods = normalizeProcessingMethodsUsed(formData.processingMethodUsed);
    // if (!methods.length) {
    //   newErrors.processingMethodUsed = "Processing Method Used is required.";
    // }

    const sources = Array.isArray(formData.energySources) ? formData.energySources : [];
    const showElectricity = sources.includes(ENERGY_SOURCE_ELECTRICITY);
    const showFuel = sources.includes(ENERGY_SOURCE_FUEL);

    if (sources.length === 0) {
      newErrors.energySources = "Select at least one: Electricity and/or Fuel.";
    }

    const electricityStr = String(formData.electricityConsumption ?? "").trim();
    const entries = Array.isArray(formData.fuelEntries) ? formData.fuelEntries : [];

    if (showElectricity) {
      if (!electricityStr) {
        newErrors.electricityConsumption = "Electricity consumption is required.";
      } else {
        const electricityNum = Number(electricityStr);
        if (isNaN(electricityNum) || electricityNum < 0) {
          newErrors.electricityConsumption =
            "Electricity Consumption must be a valid number.";
        }
      }
    }

    if (showFuel) {
      const hasAnyFuelName = entries.some((e) => String(e?.fuelName ?? "").trim() !== "");
      const hasFuelQtyOrUnit = entries.some((e) => {
        const q = String(e?.quantity ?? "").trim();
        const u = String(e?.unit ?? "").trim();
        return q !== "" || u !== "";
      });

      if (!hasAnyFuelName && !hasFuelQtyOrUnit) {
        newErrors.fuelEntries = "Add at least one fuel with name, quantity, and unit.";
      }

      entries.forEach((entry, idx) => {
        const name = String(entry?.fuelName ?? "").trim();
        const qtyStr = String(entry?.quantity ?? "").trim();
        const unitStr = String(entry?.unit ?? "").trim();
        const rowTouched = name || qtyStr || unitStr;

        if (!name && (qtyStr || unitStr)) {
          newErrors[`fuelEntries.fuelName.${idx}`] = "Fuel name is required.";
        }
        if (name) {
          if (!qtyStr) {
            newErrors[`fuelEntries.quantity.${idx}`] = "Quantity is required.";
          } else if (isNaN(Number(qtyStr)) || Number(qtyStr) < 0) {
            newErrors[`fuelEntries.quantity.${idx}`] = "Quantity must be a valid number.";
          }
          if (!unitStr) {
            newErrors[`fuelEntries.unit.${idx}`] = "Unit is required.";
          }
        } else if (rowTouched && entries.length > 1 && idx === 1) {
          if (qtyStr || unitStr) {
            newErrors[`fuelEntries.fuelName.${idx}`] = "Fuel name is required.";
          }
        }
      });

      const names = entries.map((e) => String(e?.fuelName ?? "").trim()).filter(Boolean);
      if (names.length > 1 && new Set(names).size !== names.length) {
        newErrors.fuelEntries = "Fuel names must be different.";
      }
    }

    const processingEntries = Array.isArray(formData.processingMethodEntries)
      ? formData.processingMethodEntries
      : [];

    const hasAnyMaterialName = processingEntries.some(
      (e) => String(e?.methodName ?? "").trim() !== ""
    );
    const hasAnyMaterialQty = processingEntries.some((e) => {
      const q = String(e?.quantity ?? "").trim();
      return q !== "";
    });

    if (!hasAnyMaterialName && !hasAnyMaterialQty) {
      newErrors.processingMethodEntries =
        "Add at least one material with name, quantity.";
    }

    processingEntries.forEach((entry, idx) => {
      const name = String(entry?.methodName ?? "").trim();
      const qtyStr = String(entry?.quantity ?? "").trim();
      const rowTouched = name || qtyStr;

      if (!name && qtyStr) {
        newErrors[`processingMethodEntries.methodName.${idx}`] =
          "Material is required.";
      }
      if (name) {
        if (!qtyStr) {
          newErrors[`processingMethodEntries.quantity.${idx}`] =
            "Quantity is required.";
        } else if (isNaN(Number(qtyStr)) || Number(qtyStr) < 0) {
          newErrors[`processingMethodEntries.quantity.${idx}`] =
            "Quantity must be a valid number.";
        }
      } else if (rowTouched && processingEntries.length > 1 && idx === 1) {
        if (qtyStr) {
          newErrors[`processingMethodEntries.methodName.${idx}`] =
            "Material is required.";
        }
      }
    });

    const methodNames = processingEntries
      .map((e) => String(e?.methodName ?? "").trim())
      .filter(Boolean);
    if (methodNames.length > 1 && new Set(methodNames).size !== methodNames.length) {
      newErrors.processingMethodEntries = "Materials must be different.";
    }

    if (user?.endUser && isEditing && !formData.editRemarkReason) {
      newErrors.editRemarkReason = "Edit remark is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;

    const showElectricity = (formData.energySources || []).includes(
      ENERGY_SOURCE_ELECTRICITY
    );
    const showFuel = (formData.energySources || []).includes(ENERGY_SOURCE_FUEL);

    const apiFuelEntries = showFuel
      ? (formData.fuelEntries || [])
        .filter((e) => String(e?.fuelName ?? "").trim())
        .map((e) => ({
          fuelType: e.fuelName,
          fuelName: e.fuelName,
          fuelConsumption:
            e.quantity !== "" && e.quantity != null ? Number(e.quantity) : null,
          fuelConsumptionUnit: e.unit || "",
        }))
      : [];

    const firstFuel = apiFuelEntries[0] || {};
    const secondFuel = apiFuelEntries[1] || {};

    const elecValue =
      showElectricity && formData.electricityConsumption
        ? Number(formData.electricityConsumption)
        : "";

    const apiProcessingEntries = (formData.processingMethodEntries || [])
      .filter((entry) => {
        const name = String(entry?.methodName ?? "").trim();
        return name && wasteTypesArray.includes(name);
      })
      .map((entry) => ({
        methodName: entry.methodName,
        quantity:
          entry.quantity !== "" && entry.quantity != null
            ? Number(entry.quantity)
            : null,
      }));

    let formDataToSubmit = {
      soldProductDate: formData.soldProductDate,
      productMasterId: formData.productMasterId,
      productId: formData.productId,
      productName: formData.productName,
      productCategory: formData.productCategory,
      customerMasterId: formData.customerMasterId,
      vendorName: formData.vendorName,
      quantitySold: {
        value: formData.quantitySold ? parseFloat(formData.quantitySold) : null,
        unit: formData.quantitySoldUnit,
      },
      locationOfProcessing: formData.locationOfProcessing,
      // processingMethodUsed: normalizeProcessingMethodsUsed(formData.processingMethodUsed),
      electricityConsumption: elecValue,
      energyConsumptionForProcessing: elecValue,
      fuelEntries: apiFuelEntries,
      fuelType: firstFuel.fuelType || "",
      fuelConsumption: firstFuel.fuelConsumption ?? "",
      fuelConsumptionUnit: firstFuel.fuelConsumptionUnit || "",
      fuelType2: secondFuel.fuelType || "",
      fuelConsumption2:
        secondFuel.fuelConsumption !== undefined && secondFuel.fuelConsumption !== null
          ? secondFuel.fuelConsumption
          : "",
      fuelConsumptionUnit2: secondFuel.fuelConsumptionUnit || "",
      energySourceForProcessing: apiFuelEntries
        .map((f) => `${f.fuelType || ""}`.trim())
        .filter(Boolean)
        .join(", "),
      energySources: formData.energySources || [],
      processingMethodEntries: apiProcessingEntries,
      materialsAddedDuringProcessing: apiProcessingEntries.map((entry) =>
        entry.quantity != null && !Number.isNaN(entry.quantity)
          ? `${entry.methodName} ${entry.quantity} MT`
          : entry.methodName
      ),
      remark: formData.remark,
      documents: uploadedFiles,
    };

    if (user?.endUser && isEditing) {
      formDataToSubmit.requestStatus = "edit";
      formDataToSubmit.requestMode = true;
      formDataToSubmit.approvalStatus = "Pending";
      formDataToSubmit.editRemarkReason = formData.editRemarkReason;
    }

    onSubmit(formDataToSubmit, isEditing);
    setUploadedFiles([]);
  };

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNumericChange = (field) => (event) => {
    const { value } = event.target;
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleElectricityChange = (event) => {
    const { value } = event.target;
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, electricityConsumption: value }));
    setErrors((prev) => ({
      ...prev,
      electricityConsumption: "",
    }));
  };

  const handleEnergySourcesChange = (event) => {
    const raw = event.target.value;
    const next =
      typeof raw === "string"
        ? raw.split(",").filter(Boolean)
        : [...raw].filter(Boolean);
    setFormData((prev) => {
      const updates = { energySources: next };
      if (!next.includes(ENERGY_SOURCE_ELECTRICITY)) {
        updates.electricityConsumption = "";
      }
      if (!next.includes(ENERGY_SOURCE_FUEL)) {
        updates.fuelEntries = [emptyFuelRow()];
      }
      return { ...prev, ...updates };
    });
    setErrors((prev) => {
      const n = { ...prev, energySources: "" };
      if (!next.includes(ENERGY_SOURCE_ELECTRICITY)) {
        delete n.electricityConsumption;
      }
      if (!next.includes(ENERGY_SOURCE_FUEL)) {
        Object.keys(n).forEach((k) => {
          if (k.startsWith("fuelEntries.")) delete n[k];
        });
        delete n.fuelEntries;
      }
      return n;
    });
  };

  const isProcessingMethodEntryComplete = (entry) =>
    !!entry &&
    String(entry.methodName ?? "").trim() !== "" &&
    String(entry.quantity ?? "").trim() !== "";

  const getSelectedProcessingMethodNames = (entries = [], currentIndex = -1) =>
    new Set(
      (Array.isArray(entries) ? entries : [])
        .filter((_, idx) => idx !== currentIndex)
        .map((entry) => String(entry?.methodName ?? "").trim())
        .filter(Boolean)
    );

  const handleAddProcessingMethodEntry = () => {
    if ((formData.processingMethodEntries || []).length >= 2) return;
    setFormData((prev) => ({
      ...prev,
      processingMethodEntries: [
        ...(prev.processingMethodEntries || []),
        emptyProcessingMethodRow(),
      ],
    }));
    setErrors((prev) => ({ ...prev, processingMethodEntries: "" }));
  };

  const handleRemoveProcessingMethodEntry = (index) => {
    setFormData((prev) => {
      const next = (prev.processingMethodEntries || []).filter(
        (_, idx) => idx !== index
      );
      const safe = next.length > 0 ? next : [emptyProcessingMethodRow()];
      return { ...prev, processingMethodEntries: safe };
    });
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith("processingMethodEntries.")) delete next[k];
      });
      delete next.processingMethodEntries;
      return next;
    });
  };

  const handleProcessingMethodEntryChange = (index, field) => (event) => {
    const { value } = event.target;
    if (field === "quantity" && value !== "" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setFormData((prev) => {
      const list = [...(prev.processingMethodEntries || [])];
      list[index] = {
        ...(list[index] || emptyProcessingMethodRow()),
        [field]: value,
      };
      return { ...prev, processingMethodEntries: list };
    });
    setErrors((prev) => ({
      ...prev,
      [`processingMethodEntries.${field}.${index}`]: "",
      processingMethodEntries: "",
    }));
  };

  const isAddProcessingMethodDisabled =
    !Array.isArray(formData.processingMethodEntries) ||
    formData.processingMethodEntries.length === 0 ||
    !formData.processingMethodEntries.every((entry) =>
      isProcessingMethodEntryComplete(entry)
    ) ||
    formData.processingMethodEntries.length >= 2;

  const isFuelEntryComplete = (entry) =>
    !!entry &&
    String(entry.fuelName ?? "").trim() !== "" &&
    String(entry.quantity ?? "").trim() !== "" &&
    String(entry.unit ?? "").trim() !== "";

  const getSelectedFuelNames = (entries = [], currentFuelIndex = -1) =>
    new Set(
      (Array.isArray(entries) ? entries : [])
        .filter((_, idx) => idx !== currentFuelIndex)
        .map((entry) => String(entry?.fuelName ?? "").trim())
        .filter(Boolean)
    );

  const handleAddFuelEntry = () => {
    if ((formData.fuelEntries || []).length >= 2) return;
    setFormData((prev) => ({
      ...prev,
      fuelEntries: [...(prev.fuelEntries || []), emptyFuelRow()],
    }));
    setErrors((prev) => ({ ...prev, fuelEntries: "" }));
  };

  const handleRemoveFuelEntry = (index) => {
    setFormData((prev) => {
      const next = (prev.fuelEntries || []).filter((_, idx) => idx !== index);
      const safe = next.length > 0 ? next : [emptyFuelRow()];
      return { ...prev, fuelEntries: safe };
    });
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith("fuelEntries.")) delete next[k];
      });
      delete next.fuelEntries;
      return next;
    });
  };

  const handleFuelEntryChange = (index, field) => (event) => {
    const { value } = event.target;
    if (field === "quantity" && value !== "" && !/^\d*\.?\d*$/.test(value)) return;

    setFormData((prev) => {
      const list = [...(prev.fuelEntries || [])];
      const row = { ...(list[index] || emptyFuelRow()), [field]: value };
      if (field === "fuelName") {
        row.unit = "";
      }
      list[index] = row;
      return { ...prev, fuelEntries: list };
    });
    setErrors((prev) => ({
      ...prev,
      [`fuelEntries.${field}.${index}`]: "",
      fuelEntries: "",
    }));
  };

  const isAddFuelDisabled =
    !Array.isArray(formData.fuelEntries) ||
    formData.fuelEntries.length === 0 ||
    !formData.fuelEntries.every((entry) => isFuelEntryComplete(entry)) ||
    formData.fuelEntries.length >= 2;

  const renderFuelFields = () => (
    <Box sx={{ margin: "1rem 0" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Fuel Consumption (You can select upto two fuel type)
      </Typography>
      <Box display="flex" gap={1} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", flex: 1.2, minWidth: 120 }}>
          Fuel Name
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", flex: 0.75, minWidth: 72 }}>
          Quantity
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", flex: 0.75, minWidth: 72 }}>
          Unit
        </Typography>
      </Box>
      {(formData.fuelEntries || []).map((fuel, fuelIndex) => (
        <Box
          display="flex"
          gap={1}
          key={`fuel-row-${fuelIndex}`}
          sx={{ mb: 1, alignItems: "flex-start" }}
        >
          <TextField
            select
            size="small"
            name={`fuelEntries.fuelName.${fuelIndex}`}
            value={fuel.fuelName}
            onChange={handleFuelEntryChange(fuelIndex, "fuelName")}
            sx={{ flex: 1.2, minWidth: 120 }}
            error={Boolean(errors[`fuelEntries.fuelName.${fuelIndex}`])}
            helperText={errors[`fuelEntries.fuelName.${fuelIndex}`] || ""}
            FormHelperTextProps={inlineErrorTextProps}
            SelectProps={{
              displayEmpty: true,
              MenuProps: unitMenuProps,
              renderValue: (selected) =>
                selected && selected !== "" ? selected : "Tap to select",
            }}
          >
            <MenuItem value="" disabled>
              Tap to select
            </MenuItem>
            {FUEL_TYPE_OPTIONS.map((fuelType) => (
              <MenuItem
                key={fuelType}
                value={fuelType}
                disabled={getSelectedFuelNames(formData.fuelEntries || [], fuelIndex).has(
                  fuelType
                )}
              >
                {fuelType}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            placeholder="Quantity"
            name={`fuelEntries.quantity.${fuelIndex}`}
            value={fuel.quantity}
            onChange={handleFuelEntryChange(fuelIndex, "quantity")}
            inputProps={{ inputMode: "decimal" }}
            sx={{ flex: 0.75, minWidth: 72 }}
            error={Boolean(errors[`fuelEntries.quantity.${fuelIndex}`])}
            helperText={errors[`fuelEntries.quantity.${fuelIndex}`] || ""}
            FormHelperTextProps={inlineErrorTextProps}
          />
          <TextField
            select
            size="small"
            placeholder="Unit"
            name={`fuelEntries.unit.${fuelIndex}`}
            value={fuel.unit}
            onChange={handleFuelEntryChange(fuelIndex, "unit")}
            disabled={!fuel.fuelName}
            sx={{ flex: 0.75, minWidth: 72 }}
            error={Boolean(errors[`fuelEntries.unit.${fuelIndex}`])}
            helperText={errors[`fuelEntries.unit.${fuelIndex}`] || ""}
            FormHelperTextProps={inlineErrorTextProps}
            SelectProps={{
              displayEmpty: true,
              MenuProps: unitMenuProps,
              renderValue: (selected) =>
                selected && selected !== "" ? selected : "Tap to Select",
            }}
          >
            <MenuItem value="" disabled>
              Tap to Select
            </MenuItem>
            {(FUEL_UNIT_BY_TYPE[fuel.fuelName] || []).map((u) => (
              <MenuItem key={u} value={u}>
                {u}
              </MenuItem>
            ))}
          </TextField>
          {(formData.fuelEntries || []).length > 1 && (
            <IconButton
              color="error"
              onClick={() => handleRemoveFuelEntry(fuelIndex)}
              sx={{ alignSelf: "center", mt: 0.5 }}
              aria-label="Remove fuel row"
            >
              <Delete />
            </IconButton>
          )}
        </Box>
      ))}
      <Button
        variant="outlined"
        onClick={handleAddFuelEntry}
        disabled={isAddFuelDisabled}
        sx={{ textTransform: "none", mt: 0.5 }}
      >
        + ADD NEW
      </Button>
      {errors.fuelEntries && typeof errors.fuelEntries === "string" && (
        <Typography variant="body2" color="#D34040" sx={{ mt: 1 }}>
          {errors.fuelEntries}
        </Typography>
      )}
    </Box>
  );

  const renderProcessingMethodFields = () => (
    <Box sx={{ margin: "1rem 0" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Materials Added During Processing (MT) (You can select upto two material)
      </Typography>
      <Box display="flex" gap={1} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", flex: 1.2, minWidth: 120 }}>
          Material
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", flex: 0.75, minWidth: 72 }}>
          Quantity (MT)
        </Typography>
      </Box>
      {(formData.processingMethodEntries || []).map((entry, entryIndex) => (
        <Box
          display="flex"
          gap={1}
          key={`processing-method-row-${entryIndex}`}
          sx={{ mb: 1, alignItems: "flex-start" }}
        >
          <TextField
            select
            size="small"
            name={`processingMethodEntries.methodName.${entryIndex}`}
            value={entry.methodName}
            onChange={handleProcessingMethodEntryChange(entryIndex, "methodName")}
            sx={{ flex: 1.2, minWidth: 120 }}
            error={Boolean(errors[`processingMethodEntries.methodName.${entryIndex}`])}
            helperText={errors[`processingMethodEntries.methodName.${entryIndex}`] || ""}
            FormHelperTextProps={inlineErrorTextProps}
            SelectProps={{
              displayEmpty: true,
              MenuProps: unitMenuProps,
              renderValue: (selected) =>
                selected && selected !== "" ? selected : "Tap to select",
            }}
          >
            <MenuItem value="" disabled>
              Tap to select
            </MenuItem>
            {materialOptions.map((methodName) => (
              <MenuItem
                key={methodName}
                value={methodName}
                disabled={getSelectedProcessingMethodNames(
                  formData.processingMethodEntries || [],
                  entryIndex
                ).has(methodName)}
              >
                {methodName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            placeholder="Quantity"
            name={`processingMethodEntries.quantity.${entryIndex}`}
            value={entry.quantity}
            onChange={handleProcessingMethodEntryChange(entryIndex, "quantity")}
            inputProps={{ inputMode: "decimal" }}
            sx={{ flex: 0.75, minWidth: 72 }}
            error={Boolean(errors[`processingMethodEntries.quantity.${entryIndex}`])}
            helperText={errors[`processingMethodEntries.quantity.${entryIndex}`] || ""}
            FormHelperTextProps={inlineErrorTextProps}
          />
          {(formData.processingMethodEntries || []).length > 1 && (
            <IconButton
              color="error"
              onClick={() => handleRemoveProcessingMethodEntry(entryIndex)}
              sx={{ alignSelf: "center", mt: 0.5 }}
              aria-label="Remove processing method row"
            >
              <Delete />
            </IconButton>
          )}
        </Box>
      ))}
      <Button
        variant="outlined"
        onClick={handleAddProcessingMethodEntry}
        disabled={isAddProcessingMethodDisabled}
        sx={{ textTransform: "none", mt: 0.5 }}
      >
        + ADD NEW
      </Button>
      {errors.processingMethodEntries &&
        typeof errors.processingMethodEntries === "string" && (
          <Typography variant="body2" color="#D34040" sx={{ mt: 1 }}>
            {errors.processingMethodEntries}
          </Typography>
        )}
    </Box>
  );

  const renderDisabledField = ({ title, value, placeholder, errorKey }) => (
    <Box sx={{ margin: "1rem 0" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
        {title}
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={value || ""}
        disabled
        placeholder={placeholder}
        sx={disabledFieldSx}
        error={Boolean(errorKey && errors[errorKey])}
        helperText={errorKey && errors[errorKey] ? errors[errorKey] : ""}
        FormHelperTextProps={inlineErrorTextProps}
      />
    </Box>
  );

  const renderSelect = ({
    title,
    name,
    value,
    options,
    onChange,
    disabled = false,
    errorKey,
  }) => (
    <Box sx={{ margin: "1rem 0" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
        {title}
      </Typography>
      <TextField
        select
        size="small"
        fullWidth
        disabled={disabled}
        value={value || "Tap to select"}
        onChange={onChange}
        error={Boolean(errors[errorKey || name])}
        helperText={
          <Typography variant="body2" color="#D34040" sx={{ marginLeft: "-0.8rem" }}>
            {errors[errorKey || name] || ""}
          </Typography>
        }
        SelectProps={{
          MenuProps: { PaperProps: { sx: { maxHeight: 280 } } },
        }}
      >
        <MenuItem value="Tap to select" disabled>
          Tap to select
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
    >
      <Box sx={{ width: "40vw", display: "flex", justifyContent: "space-between", gap: "1rem", }}>
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box className={style.main_div}>
          <Typography
            variant="h6"
            align="left"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
          >
            {header}
          </Typography>

          <form>
            {/* Sold product date */}
            <Box sx={{ margin: "1rem 0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                Sold Product Date
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={formData.soldProductDate || ""}
                onChange={handleChange("soldProductDate")}
                error={Boolean(errors.soldProductDate)}
                helperText={
                  <Typography variant="body2" color="#D34040" sx={{ marginLeft: "-0.8rem" }}>
                    {errors.soldProductDate || ""}
                  </Typography>
                }
                inputProps={{ max: new Date().toISOString().split("T")[0] }}
              />
            </Box>

            {/* Product ID */}
            {renderSelect({
              title: "Product ID",
              name: "productMasterId",
              value: formData.productMasterId,
              errorKey: "productMasterId",
              disabled: productsWithCustomers.length === 0,
              options: productsWithCustomers.map((p) => ({
                value: p._id,
                label: p.productId || p.productName,
              })),
              onChange: (e) => {
                const val = e.target.value;
                if (val === "Tap to select") return;
                const product = productsWithCustomers.find((p) => p._id === val);
                applySelectedProduct(product);
              },
            })}

            {renderDisabledField({
              title: "Product Name",
              value: formData.productName,
              placeholder: "Auto-filled from product",
            })}

            {renderDisabledField({
              title: "Product Category",
              value: formData.productCategory,
              placeholder: "Auto-filled from product",
            })}

            {/* Customer / Vendor */}
            {renderSelect({
              title: "Customer/Vendor Name",
              name: "customerMasterId",
              value: formData.customerMasterId,
              errorKey: "customerMasterId",
              disabled: !formData.productMasterId,
              options: customerOptions.map((c) => ({
                value: c._id,
                label: c.customerVendorName,
              })),
              onChange: (e) => {
                const val = e.target.value;
                if (val === "Tap to select") return;
                const customer = customerOptions.find((c) => c._id === val);
                applySelectedCustomer(customer);
              },
            })}

            {/* Quantity Sold */}
            <Box sx={{ margin: "1rem 0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                Quantity Sold (Pieces/MT/KL)
              </Typography>
              <Box sx={{ display: "flex", gap: "0.75rem" }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Enter quantity"
                  value={formData.quantitySold || ""}
                  onChange={handleNumericChange("quantitySold")}
                  error={Boolean(errors.quantitySold)}
                  helperText={errors.quantitySold || ""}
                  FormHelperTextProps={inlineErrorTextProps}
                />
                <TextField
                  select
                  size="small"
                  sx={{ width: "50%" }}
                  value={formData.quantitySoldUnit || ""}
                  onChange={handleChange("quantitySoldUnit")}
                  error={Boolean(errors.quantitySoldUnit)}
                  helperText={errors.quantitySoldUnit || ""}
                  FormHelperTextProps={inlineErrorTextProps}
                  SelectProps={{
                    displayEmpty: true,
                    MenuProps: unitMenuProps,
                    renderValue: (selected) =>
                      selected && selected !== "" ? selected : "Tap to select",
                  }}
                >
                  <MenuItem value="" disabled>
                    Tap to select
                  </MenuItem>
                  {QUANTITY_SOLD_UNITS.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>

            {renderDisabledField({
              title: "Location of Processing (Country)",
              value: formData.locationOfProcessing,
              placeholder: "Auto-filled from customer",
            })}

            {/* {renderDisabledField({
              title: "Processing Method Used",
              value: formatProcessingMethodsDisplay(
                normalizeProcessingMethodsUsed(formData.processingMethodUsed)
              ),
              placeholder: "Auto-filled from customer",
              errorKey: "processingMethodUsed",
            })} */}

            <Box sx={{ margin: "1rem 0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                Energy Source
              </Typography>
              <FormControl fullWidth size="small" error={Boolean(errors.energySources)} sx={{ mt: 0.5 }}>
                <Select
                  id="processing-energy-sources"
                  multiple
                  value={formData.energySources || []}
                  onChange={handleEnergySourcesChange}
                  displayEmpty
                  inputProps={{ "aria-label": "Energy Source" }}
                  renderValue={(selected) => {
                    const list = Array.isArray(selected) ? selected : [];
                    if (list.length === 0) return "Tap to select";
                    return list
                      .map((v) =>
                        v === ENERGY_SOURCE_ELECTRICITY ? "Electricity" : "Fuel"
                      )
                      .join(", ");
                  }}
                  MenuProps={unitMenuProps}
                  sx={(theme) => ({
                    ...(!(formData.energySources || []).length && {
                      "& .MuiSelect-select": {
                        color: theme.palette.text.secondary,
                      },
                    }),
                  })}
                >
                  <MenuItem value={ENERGY_SOURCE_ELECTRICITY}>Electricity</MenuItem>
                  <MenuItem value={ENERGY_SOURCE_FUEL}>Fuel</MenuItem>
                </Select>
                {errors.energySources ? (
                  <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                    {errors.energySources}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Select Electricity and/or Fuel. Only selected sections are shown below.
                  </Typography>
                )}
              </FormControl>
            </Box>

            {(formData.energySources || []).includes(ENERGY_SOURCE_ELECTRICITY) && (
              <Box sx={{ margin: "1rem 0" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                  {formatTitleWithOptional("Electricity Consumption (KWH)")}
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Enter electricity consumption"
                  value={formData.electricityConsumption || ""}
                  onChange={handleElectricityChange}
                  error={Boolean(errors.electricityConsumption)}
                  helperText={errors.electricityConsumption || ""}
                  FormHelperTextProps={inlineErrorTextProps}
                />
              </Box>
            )}

            {(formData.energySources || []).includes(ENERGY_SOURCE_FUEL) && renderFuelFields()}

            {renderProcessingMethodFields()}

            {/* Remark */}
            <Box sx={{ margin: "1rem 0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                {formatTitleWithOptional("Remark")}
              </Typography>
              <TextField
                multiline
                rows={3}
                size="small"
                fullWidth
                placeholder="Remark"
                value={formData.remark || ""}
                onChange={handleChange("remark")}
              />
            </Box>

            {user?.endUser && isEditing && (
              <Multiline
                title={formatTitleWithOptional("Edit Remark Reason")}
                placeholder="Additional edit remarks"
                fieldName="editRemarkReason"
                value={formData.editRemarkReason}
                handleChange={handleChange("editRemarkReason")}
                errors={errors.editRemarkReason}
              />
            )}

            <FileField
              title={formatTitleWithOptional("Upload Documents (optional)")}
              name="document"
              fileUrls={uploadedFiles}
              progress={uploadProgress.document || []}
              {...{ setUploadedFiles, setUploadProgress, setErrors }}
            />
          </form>

          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              backgroundColor: "#1C486B",
              textTransform: "none",
              mt: 2,
            }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;
